import { Link } from "@tanstack/react-router";
import { FEMALE_ON_GRASS } from "@/lib/photos";

/**
 * "Know it when you see it": the CDC photograph of an adult female, annotated the way a
 * field guide plate is. Each callout repeats a mark the identification guide already
 * states and sources; positions are percentages of the photo, so the leaders stay on
 * their features at any width.
 */
const MARKS = [
  {
    n: 1,
    at: { x: 47, y: 49.5 },
    label: { x: 66, y: 30 },
    title: "One pale spot on the back",
    body: "The “lone star.” Only adult females have it. Males have pale streaks near the rear edge instead.",
  },
  {
    n: 2,
    at: { x: 18, y: 42 },
    label: { x: 8, y: 22 },
    title: "Long mouthparts",
    body: "Clearly visible from above, longer than a dog tick’s.",
  },
] as const;

export function HomeSpecimen() {
  return (
    <section aria-labelledby="home-specimen" className="mx-auto max-w-6xl px-4 pb-16 sm:px-6">
      <h2 id="home-specimen" className="label-caps border-t border-rule pt-3 text-muted-foreground">
        Know it when you see it
      </h2>
      <div className="mt-6 grid items-start gap-x-14 gap-y-8 md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:mt-8">
        <figure>
          <div className="relative">
            <img
              src={FEMALE_ON_GRASS.src}
              width={FEMALE_ON_GRASS.width}
              height={FEMALE_ON_GRASS.height}
              loading="lazy"
              decoding="async"
              alt={FEMALE_ON_GRASS.alt}
              className="block h-auto w-full"
            />
            <svg
              aria-hidden
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              className="pointer-events-none absolute inset-0 h-full w-full"
            >
              {MARKS.map((m) => (
                <g key={m.n}>
                  <line
                    x1={m.label.x}
                    y1={m.label.y}
                    x2={m.at.x}
                    y2={m.at.y}
                    stroke="#f4e7c6"
                    strokeWidth={1.25}
                    vectorEffect="non-scaling-stroke"
                  />
                  <circle
                    cx={m.at.x}
                    cy={m.at.y}
                    r={0.9}
                    fill="#f4e7c6"
                    vectorEffect="non-scaling-stroke"
                  />
                </g>
              ))}
            </svg>
            {MARKS.map((m) => (
              <span
                key={m.n}
                aria-hidden
                className="absolute grid h-7 w-7 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-cream font-display text-sm font-semibold text-[#121e17] ring-1 ring-black/30"
                style={{ left: `${m.label.x}%`, top: `${m.label.y}%` }}
              >
                {m.n}
              </span>
            ))}
          </div>
          <figcaption className="mt-3 text-sm leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">Fig. 2.</span> Adult female, seen from
            above. Photo: James Gathany, CDC. Public Health Image Library (phil.cdc.gov) #4407.
            Public domain.
          </figcaption>
        </figure>

        <div>
          <p className="font-display text-[1.75rem] font-medium leading-tight text-foreground sm:text-[2rem]">
            A reddish-brown tick about the size of a sesame seed.
          </p>
          <ol className="mt-6 divide-y divide-border border-y border-border">
            {MARKS.map((m) => (
              <li key={m.n} className="grid grid-cols-[2rem_1fr] gap-x-3 py-4">
                <span className="tabular font-display text-lg text-rust-ink">{m.n}</span>
                <span>
                  <span className="block font-semibold text-foreground">{m.title}</span>
                  <span className="mt-0.5 block text-[0.9375rem] leading-relaxed text-muted-foreground">
                    {m.body}
                  </span>
                </span>
              </li>
            ))}
            <li className="grid grid-cols-[2rem_1fr] gap-x-3 py-4">
              <span className="tabular font-display text-lg text-rust-ink">3</span>
              <span>
                <span className="block font-semibold text-foreground">
                  Small, and smaller when young
                </span>
                <span className="mt-0.5 block text-[0.9375rem] leading-relaxed text-muted-foreground">
                  Adults are 3–4 mm before feeding. Nymphs, which bite too, are about the size of a
                  poppy seed and have no spot.
                </span>
              </span>
            </li>
          </ol>
          <Link
            to="/tick-identification"
            className="mt-6 inline-flex rounded-sm border border-foreground/30 px-5 py-3 text-sm font-semibold text-foreground hover:border-foreground"
          >
            Identify a tick you found
          </Link>
        </div>
      </div>
    </section>
  );
}
